import { Checkbox, Tooltip } from "@blueprintjs/core";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import { IResultadoItem } from "../../../model";
import { AppStore } from "../../../stores/AppStore";
import { ResultadoStore } from "../../../stores/resultados/ResultadoStore";
import { NotaItemDialog } from "./item-detail/ItemDialogNota";
import { AppIcon } from "../../utils/AppIcon";

const numeral = require("numeral");

interface Props {
  items: IResultadoItem[];
  modelo: string;
  entrada_saida: string;
  data: any;
}

const classificacaoMap: Record<string, { label: string; short: string; icon: string; color: string }> = {
  "REVENDA": { label: "Mercadoria para Revenda", short: "Revenda", icon: "currency_exchange", color: "#1F77B4" },
  "USO E CONSUMO": { label: "Material de Uso e Consumo", short: "Uso/Consumo", icon: "work", color: "#2CA02C" },
  "ATIVO IMOBILIZADO": { label: "Ativo Imobilizado", short: "Ativo Imob.", icon: "barcode", color: "#D62728" },
  "TRANSFERÊNCIA": { label: "Transferência de Mercadoria", short: "Transf.", icon: "swap_horiz", color: "#9467BD" },
  "CONSERTO": { label: "Retorno Remessa para Conserto", short: "Conserto", icon: "build", color: "#FF7F0E" },
  "COMODATO": { label: "Remessa em Comodato", short: "Comodato", icon: "handshake", color: "#E377C2" },
  "COMBUSTÍVEL USO": { label: "Combustível p/ Uso e Consumo", short: "Comb. Uso", icon: "local_gas_station", color: "#BCBD22" },
  "COMBUSTÍVEL REVENDA": { label: "Combustível p/ Revenda", short: "Comb. Rev.", icon: "sell", color: "#0F766E" },
  "SERVIÇO": { label: "Prestação de Serviço", short: "Serviço", icon: "engineering", color: "#17BECF" },
  "INDUSTRIALIZAÇÃO": { label: "Insumos p/ Industrialização", short: "Indústria", icon: "precision_manufacturing", color: "#8C564B" },
  "ESTOQUE": { label: "Baixa de Estoque", short: "Estoque", icon: "remove_shopping_cart", color: "#4d4d4d" },
  "BONIFICAÇÃO": { label: "Bonificação", short: "Bonific.", icon: "redeem", color: "#00A6FB" },
  "MERCADORIA": { label: "Retorno de Mercadoria", short: "Ret. Merc.", icon: "assignment_return", color: "#3A0CA3" },
  "GARANTIA": { label: "Remessa em Garantia", short: "Garantia", icon: "verified", color: "#00B894" },
  "FUTURA": { label: "Remessa p/ Entrega Futura", short: "Futura", icon: "schedule", color: "#c78e06" },
  "DEVOLUÇÃO": { label: "Devolução de Venda", short: "Devolução", icon: "undo", color: "#C1121F" },
  "OUTRAS ENTRADAS": { label: "Outras entradas não especificadas", short: "Outras", icon: "more_horiz", color: "#475569" },
  "DEMONSTRAÇÃO": { label: "Entrada de Mercadoria para Demonstração", short: "Demonst.", icon: "visibility", color: "#7C3AED" },
  "CONSIGNAÇÃO": { label: "Entrada de Mercadoria em Consignação", short: "Consign.", icon: "inventory_2", color: "#B45309" },
};

@observer
export class DetalhamentoNfeTable extends React.Component<Props, {}> {

  obs = observable({
    viewing: null as IResultadoItem,
  });

  handleContest(item: IResultadoItem) {
    AppStore.setContestarItem(item);
  }

  handleView(item: IResultadoItem) {
    this.obs.viewing = item;
  }

  handleViewClose = async () => {
    this.obs.viewing = null;
  };

  onSelectAll = async (isSelect: boolean) => {
    if (isSelect) {
      let data = this.props.items
        ?.filter(item => ResultadoStore.obs.itens_processando.indexOf(item.id) === -1)
        .map(item => ({
          id: item.id,
          data: item.data,
        }));
      ResultadoStore.setContestarItemsList(data);
    } else {
      ResultadoStore.resetContestarItems();
    }
  };

  onSelect = async (item: IResultadoItem, checked: boolean) => {
    let data = { id: item.id, data: item.data };
    if (checked) {
      ResultadoStore.setContestarItems(data);
    } else {
      ResultadoStore.deleteByIdContestarItems(item.id);
    }
  };

  isSelected(id: string) {
    return ResultadoStore.obs.contestarItems?.some(v => v.id === id) || false;
  }

  isProcessing(id: string) {
    return ResultadoStore.obs.itens_processando.indexOf(id) !== -1;
  }

  isAllSelected() {
    const selectable = this.props.items?.filter(item => !this.isProcessing(item.id)) || [];
    if (!selectable.length) return false;
    return selectable.every(item => this.isSelected(item.id));
  }

  getCardWarningClass(item: IResultadoItem): boolean {
    let valorIcmsAnt = item.valor_icms_ant;
    let classificacaoId = item.classificacao_id;
    let nomeClassificacao = item.classificacao_nome;
    const excludedNcms = ["27101921", "84133010", "31021010", "27101259"];

    if (item.devolucao) return true;

    if (classificacaoId) {
      if (
        nomeClassificacao === "Substituição Tributária" && valorIcmsAnt == "0" &&
        !excludedNcms.includes(item.ncm) && item.uf_origem !== "PA"
      ) return true;
    } else {
      if (!excludedNcms.includes(item.ncm) && item.uf_origem !== "PA") return true;
    }

    return false;
  }

  renderClassificacao(item: IResultadoItem) {
    const classif = classificacaoMap[item.classificacao_cliente_final];
    if (!classif) return null;

    return (
      <Tooltip content={classif.label}>
        <span className="detalhamento-action-btn detalhamento-class-btn" style={{ borderColor: classif.color + "40", background: classif.color + "12" }}>
          <AppIcon name={classif.icon} color={classif.color} size={18} />
        </span>
      </Tooltip>
    );
  }

  renderActions(item: IResultadoItem) {
    if (!AppStore.obs1.usuario) return null;

    if (this.isProcessing(item.id)) {
      return (
        <div className="detalhamento-card-actions">
          <button className="detalhamento-action-btn" onClick={() => this.handleView(item)}>
            <img src="/images/contestar.svg" width={20} />
          </button>
          <Tooltip content="Processando...">
            <img src="/images/icone-relogio.png" width={24} />
          </Tooltip>
        </div>
      );
    }

    return (
      <div className="detalhamento-card-actions">
        <Tooltip content="Detalhes">
          <button className="detalhamento-action-btn" onClick={() => this.handleView(item)}>
            <img src="/images/contestar.svg" width={20} />
          </button>
        </Tooltip>
        {item.calculo_memoria_salva 
          ? <Tooltip content="Contestado via memória">
            <button className="detalhamento-action-btn" onClick={() => this.handleContest(item)}>
              <img src="/images/contestado_memoria.png" width={20} />
            </button>
          </Tooltip>
          : item.contestacao_calc_id
            ? <Tooltip content="Contestado">
              <button className="detalhamento-action-btn" onClick={() => this.handleContest(item)}>
                <img src="/images/contestado.png" width={20} />
              </button>
            </Tooltip>
            : <Tooltip content="Contestar">
              <button className="detalhamento-action-btn" onClick={() => this.handleContest(item)}>
                <img src="/images/contestar.png" width={20} />
              </button>
            </Tooltip>
        }
      </div>
    );
  }

  renderViewDialog() {
    return (
      <NotaItemDialog
        modelo={this.props.modelo}
        item={this.obs.viewing}
        entrada_saida={this.props.entrada_saida}
        data={this.props.data}
        onClose={this.handleViewClose}
      />
    );
  }

  renderCard(item: IResultadoItem) {
    const selected = this.isSelected(item.id);
    const isWarning = this.getCardWarningClass(item);
    const isProcessing = this.isProcessing(item.id);

    const valorIcmsAnt = parseFloat(item.valor_icms_ant) || 0;
    const valorContestado = parseFloat(item.valor_contestacao_icms) || 0;
    const valorPagar = valorIcmsAnt - valorContestado;
    const isRetroativa = ResultadoStore.obs.notasDivergentes;

    const classificacaoNome = (item.classificacao_nome && item.classificacao_nome.startsWith("1"))
      ? item.classificacao_nome.substring(0, 4)
      : "Sem";

    const cardClasses = [
      "detalhamento-card",
      isWarning ? "card-warning" : "",
      selected ? "card-selected" : "",
    ].filter(Boolean).join(" ");

    return (
      <div key={item.id} className={cardClasses}>
        {/* Header */}
        <div className="detalhamento-card-header">
          <div className="detalhamento-card-header-left">
            {!isProcessing && (
              <Checkbox
                checked={selected}
                onChange={(e) => this.onSelect(item, (e.target as HTMLInputElement).checked)}
                style={{ marginBottom: 0 }}
              />
            )}
            <span className="detalhamento-card-descricao">
              {item.descricao?.toUpperCase()}
            </span>
            {this.renderClassificacao(item)}
          </div>
          {this.renderActions(item)}
        </div>

        {/* Body */}
        <div className="detalhamento-card-body">
          <div className="detalhamento-field">
            <span className="detalhamento-field-label">Série / Nota</span>
            <span className="detalhamento-field-value">
              {item.serie_nota} / {item.num_nota || ""} - {item.num_item}
            </span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">Emissão</span>
            <span className="detalhamento-field-value">{moment(item.data).format("DD/MM/YYYY")}</span>
          </div>

          {isRetroativa && (
            <div className="detalhamento-field">
              <span className="detalhamento-field-label">Autorização</span>
              <span className="detalhamento-field-value">{moment(item.dhrecbto).format("DD/MM/YYYY")}</span>
            </div>
          )}

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">Dt. Cálculo</span>
            <span className="detalhamento-field-value">{moment(item.data_calculo).format("DD/MM/YYYY")}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">CST</span>
            <span className="detalhamento-field-value">{item.cst || "-"}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">PIS / COFINS</span>
            <span className="detalhamento-field-value">{(item.pis || "")} / {(item.cofins || "")}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">CFOP</span>
            <span className="detalhamento-field-value">{item.cfop || "-"}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">NCM</span>
            <span className="detalhamento-field-value">{item.ncm}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">CEST</span>
            <span className="detalhamento-field-value">{item.cest || "Sem CEST"}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">Produto</span>
            <span className="detalhamento-field-value">
              {numeral(parseFloat(item.valor_produto)).format("$ 0,0.00")}
              {item.devolucao && <span style={{ color: "#D62728", marginLeft: 4, fontSize: 10, fontWeight: 700 }}>(DEV)</span>}
            </span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">ICMS Class.</span>
            <span className="detalhamento-field-value">{classificacaoNome}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">ICMS Calc.</span>
            <span className="detalhamento-field-value">{numeral(valorIcmsAnt).format("$ 0,0.00")}</span>
          </div>

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">Alíq. ICMS</span>
            <span className="detalhamento-field-value">{(parseFloat(item.aliq_icms) || 0).toFixed(2)}%</span>
          </div>

          {isRetroativa && (
            <div className="detalhamento-field">
              <span className="detalhamento-field-label">Contestado</span>
              <span className="detalhamento-field-value">{numeral(valorContestado).format("$ 0,0.00")}</span>
            </div>
          )}

          <div className="detalhamento-field">
            <span className="detalhamento-field-label">A Pagar</span>
            <span className="detalhamento-field-value valor-pagar">{numeral(valorPagar).format("$ 0,0.00")}</span>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const allSelected = this.isAllSelected();
    const totalItems = this.props.items?.length || 0;
    const selectedCount = ResultadoStore.obs.contestarItems?.length || 0;

    return (
      <>
        <div className="detalhamento-container">
          <div className="detalhamento-select-all">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Checkbox
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                onChange={() => this.onSelectAll(!allSelected)}
                style={{ marginBottom: 0 }}
              />
              <span>
                {selectedCount > 0
                  ? `${selectedCount} de ${totalItems} selecionado(s)`
                  : `${totalItems} resultado(s)`
                }
              </span>
            </div>
          </div>

          {this.props.items?.map(item => this.renderCard(item))}
        </div>

        {this.obs.viewing && this.renderViewDialog()}
      </>
    );
  }
}

export default DetalhamentoNfeTable;
